import { ThemeService } from '../shared/services/theme.service';

import {
  Component,
  effect,
  inject,
  Injector,
  input,
  signal,
  untracked,
} from '@angular/core';

import * as Highcharts from 'highcharts';
import HC_accessibility from 'highcharts/modules/accessibility';
import HC_exporting from 'highcharts/modules/exporting';

HC_exporting(Highcharts);
HC_accessibility(Highcharts);
import { CommonModule } from '@angular/common';

import { TranslateService } from '@ngx-translate/core';

import { HighchartsChartModule } from 'highcharts-angular';
import moment from 'moment';

export const MODULES = [CommonModule, HighchartsChartModule];

@Component({
  selector: 'app-epp-meteogram',
  templateUrl: './epp-meteogram.component.html',
  styleUrls: ['./epp-meteogram.component.scss'],
  imports: [...MODULES],
})
export class EppMeteogramComponent {
  private injector = inject(Injector);
  public translate = inject(TranslateService);
  private themeService = inject(ThemeService);

  eppData = input<any>();
  rebuild = input<number>();
  dashboardView = input<boolean>(false);
  parameterset = input<string>();
  userParameters = input<any>();
  parameterDefinitions = input<any>();
  resolution = input<any>();

  Highcharts = Highcharts;
  chartConstructor = 'chart';
  chartOptions = {};
  chartCallback = null;
  chartRef: Highcharts.Chart;
  updateFlag = false;
  havePowerProduction = false;

  highChartsData = signal([]);
  showImperialUnits = false;

  defaultLineChartParameters = [
    'tt',
    'tx',
    'tn',
    'td',
    'road_rst',
    'bridge_rst',
    'bike_rst',
    'foot_rst',
    'rail_tt',
    'rail_tx',
    'rail_tn',
    'overhead_tt',
    'overhead_tx',
    'overhead_tn',
    'wing_rst',
    'wing_rst_max',
    'wing_rst_min',
    'tt_F',
    'tx_F',
    'tn_F',
    'td_F',
    'road_rst_F',
    'bridge_rst_F',
    'bike_rst_F',
    'foot_rst_F',
    'rail_tt_F',
    'rail_tx_F',
    'rail_tn_F',
    'overhead_tt_F',
    'overhead_tx_F',
    'overhead_tn_F',
    'wing_rst_F',
    'wing_rst_max_F',
    'wing_rst_min_F',
    'blade_rst',
  ];
  defaultBarChartParameters = [
    'windpower',
    'solarpower',
    'rr',
    'sn',
    'rr_in',
    'sn_in',
  ];

  metoceanLineChartParameters = ['ff_kn', 'ffx_kn', 'ff50_kn', 'ff100_kn'];
  metoceanBarChartParameters = [
    'total_wave_hs',
    'total_wave_hmax',
    'swell_height',
    'wave_height',
  ];

  get lineChartParameters(): string[] {
    return this.isMetocean()
      ? this.metoceanLineChartParameters
      : this.defaultLineChartParameters;
  }

  get barChartParameters(): string[] {
    return this.isMetocean()
      ? this.metoceanBarChartParameters
      : this.defaultBarChartParameters;
  }

  constructor() {
    this.listenToRebuildChanges();
    this.listenToThemeChanges();
  }

  private isDarkMode(): boolean {
    return this.themeService.theme() === 'dark';
  }

  isMetocean(): boolean {
    return this.parameterset() === 'met-ocean';
  }

  onChartInstance(chart: Highcharts.Chart) {
    this.chartRef = chart;
  }

  getHighChartsBarData(): Array<any> {
    const barData = [];
    const startDtg = moment().subtract(24, 'hours');
    const isMetocean = this.isMetocean();
    for (const parameter of this.barChartParameters) {
      if (!this.dashboardView() && this.isHiddenParameter(parameter)) continue;
      if (this.havePowerProduction && parameter.indexOf('power') < 0) continue;
      const dataForParameter = [];
      let sum = 0;
      let barCount = 0;
      const unitSnow = parameter.indexOf('_in') > 0 ? 'in' : 'cm';
      const unitRR = parameter.indexOf('_in') > 0 ? 'in' : 'mm';
      const unit = isMetocean
        ? 'm'
        : parameter.indexOf('sn') > -1
          ? unitSnow
          : unitRR;
      for (const dataset of this.eppData()) {
        if (
          dataset.hasOwnProperty(parameter) &&
          dataset[parameter] !== undefined
        ) {
          if (parameter === 'windpower' || parameter === 'solarpower') {
            this.havePowerProduction = true;
          }
          if (moment(dataset['valid_at']).isAfter(startDtg)) {
            dataForParameter.push({
              x: Date.parse(dataset['valid_at']),
              y: dataset[parameter],
            });
            sum += dataset[parameter];
            barCount += dataset[parameter] > 0 ? 1 : 0;
          }
        }
      }
      const showBar = sum > 0;
      if (dataForParameter.length > 0) {
        const highChartsPlot = {
          name: this.translate.instant('METPARA_EPP.' + parameter),
          type: 'column',
          yAxis: 1,
          data: dataForParameter,
          pointPadding: 0.01,
          maxPointWidth: barCount > 50 ? 5 : 30,
          opacity: 0.7,
          tooltip: {
            valueDecimals: 1,
            valueSuffix: this.havePowerProduction ? ' kWh' : ' ' + unit,
          },
          color: this.parameterColor(parameter),
          visible: showBar,
        };
        barData.push(highChartsPlot);
      }
    }
    return barData;
  }

  getHighChartsLineData(): Array<any> {
    const lineData = [];
    const startDtg = moment().subtract(24, 'hours');
    const isMetocean = this.isMetocean();
    for (const parameter of this.lineChartParameters) {
      if (!this.dashboardView() && this.isHiddenParameter(parameter)) continue;

      const dataForParameter = [];
      const unit = isMetocean
        ? 'kn'
        : parameter.indexOf('_F') > 0
          ? '°F'
          : '°C';
      for (const dataset of this.eppData()) {
        if (
          dataset.hasOwnProperty(parameter) &&
          dataset[parameter] !== undefined
        ) {
          if (moment(dataset['valid_at']).isAfter(startDtg)) {
            if (!isMetocean && parameter.indexOf('_F') > 0)
              this.showImperialUnits = true;
            dataForParameter.push({
              x: Date.parse(dataset['valid_at']),
              y: dataset[parameter],
            });
          }
        }
      }
      if (dataForParameter.length > 0) {
        const highChartsPlot = {
          name: this.translate.instant('METPARA_EPP.' + parameter),
          type: 'spline',
          data: dataForParameter,
          tooltip: {
            valueDecimals: 1,
            valueSuffix: ' ' + unit,
          },
          color: this.parameterColor(parameter),
          marker: {
            enabled: false,
          },
        };
        lineData.push(highChartsPlot);
      }
    }
    return lineData;
  }

  parameterColor(parameter: string): string {
    switch (parameter) {
      case 'tt':
      case 'tt_F':
      case 'tx':
        return '#F23D3D';
      case 'tn':
      case 'tn_F':
        return '#357be6';
      case 'td':
      case 'td_F':
        return '#399DE6';
      case 'road_rst':
      case 'road_rst_F':
        return '#001540';
      case 'bridge_rst':
      case 'bridge_rst_F':
        return '#b3b3b3';
      case 'bike_rst':
      case 'bike_rst_F':
        return '#b30086';
      case 'foot_rst':
      case 'foot_rst_F':
        return '#994d00';
      case 'rail_tt':
      case 'rail_tt_F':
        return '#001540';
      case 'overhead_tt':
      case 'overhead_tt_F':
        return '#b3b3b3';
      case 'rr':
      case 'rr_in':
        return '#93C9F1';
      case 'sn':
      case 'sn_in':
        return '#F79595';
      case 'rail_tx':
      case 'rail_tx_F':
        return '#f4a259';
      case 'rail_tn':
      case 'rail_tn_F':
        return '#ffd166';
      case 'overhead_tx':
      case 'overhead_tx_F':
        return '#084c61';
      case 'overhead_tn':
      case 'overhead_tn_F':
        return '#a1c181';
      case 'wing_rst':
      case 'wing_rst_F':
        return '#b3b3b3';
      case 'wing_rst_max':
      case 'wing_rst_max_F':
        return '#b47744';
      case 'wing_rst_min':
      case 'wing_rst_min_F':
        return '#525d72';
      case 'windpower':
        return '#1a59c5';
      case 'solarpower':
        return '#ffd166';
      case 'power':
        return this.parameterset() === 'energy-wind-power'
          ? '#1a59c5'
          : '#ffd166';
      case 'ff_kn':
        return '#1a59c5';
      case 'ffx_kn':
        return '#f4a259';
      case 'ff50_kn':
        return '#357be6';
      case 'ff100_kn':
        return '#001540';
      case 'total_wave_hs':
        return '#2f80ed';
      case 'total_wave_hmax':
        return '#e0525f';
      case 'swell_height':
        return '#2ca58d';
      case 'wave_height':
        return '#c9962c';
    }
    return 'green';
  }

  setHighChartOptions(): void {
    // Stationsname
    const stationName = ''; // this.station.name;
    const isDark = this.isDarkMode();

    const textColor = isDark ? '#E0E0E3' : '#333333';
    const gridLineColor = isDark ? '#444444' : '#e6e6e6';
    const plotLineColor = isDark ? '#707073' : '#9e9e9e';

    Highcharts.setOptions({
      lang: {
        shortWeekdays: this.translate.instant('CHART.shortWeekdays'),
        printChart: this.translate.instant('CHART.printChart'),
        downloadJPEG: this.translate.instant('CHART.downloadJPEG'),
        downloadPNG: this.translate.instant('CHART.downloadPNG'),
        downloadPDF: this.translate.instant('CHART.downloadPDF'),
        downloadSVG: this.translate.instant('CHART.downloadSVG'),
        viewFullscreen: this.translate.instant('CHART.viewFullscreen'),
      },
      time: {
        // timezone: this.eopService.getTimeZone(),
        useUTC: false,
      },
      chart: {
        backgroundColor: 'transparent',
        style: { fontFamily: 'Roboto, sans-serif' },
      },
    });

    const dashboardView = this.dashboardView();
    const isMetocean = this.isMetocean();
    this.chartOptions = {
      chart: {
        zoomType: 'xy',
        marginLeft: dashboardView ? 50 : 75,
        marginRight: dashboardView ? 60 : 90,
      },
      title: {
        text: stationName,
        align: 'left',
        style: { color: textColor },
      },
      xAxis: [
        {
          type: 'datetime',
          labels: {
            format: '{value:%a %H:%M}',
            style: { color: textColor }, // X-Achsen Beschriftung
          },
          crosshair: true,
          gridLineColor: gridLineColor,
        },
      ],
      legend: {
        enabled: true, // !dashboardView,
        reversed: false,
        itemStyle: { color: textColor }, // Legendentext
        itemHoverStyle: { color: '#FFF' },
      },
      yAxis: [
        {
          // Primary yAxis (Temperatur / Wind)
          gridLineColor: gridLineColor,
          labels: {
            format: isMetocean
              ? '{value} kn'
              : '{value}°' + (this.showImperialUnits ? 'F' : 'C'),
            style: { color: textColor },
          },
          title: {
            enabled: !dashboardView,
            text: isMetocean
              ? this.translate.instant('METPARA.ChartFF', { unit: 'kn' })
              : this.translate.instant('METPARA.ChartTT', {
                  unit: this.showImperialUnits ? '°F' : '°C',
                }),
            style: { color: textColor },
          },
          softMin: 0,
          plotLines: isMetocean
            ? []
            : [
                {
                  value: 0,
                  color: plotLineColor, // Angepasste Nulllinie
                  width: 1,
                  zIndex: 2,
                },
              ],
          startOnTick: false,
        },
        {
          // Secondary yAxis (Regen/Power / Wellenhöhe)
          gridLineWidth: 0,
          title: {
            enabled: !dashboardView,
            text: isMetocean
              ? this.translate.instant('METPARA.ChartWaveHeight', {
                  unit: 'm',
                })
              : this.translate.instant(
                  'METPARA.' +
                    (this.havePowerProduction ? 'ChartPower' : 'ChartRR'),
                  {
                    unit: this.havePowerProduction
                      ? 'kWh'
                      : this.showImperialUnits
                        ? 'in'
                        : 'mm',
                  }
                ),
            style: {
              // Behalte deine ParameterColor bei, sorge aber im Service für Dark-Varianten
              color: isMetocean
                ? this.parameterColor('total_wave_hs')
                : this.havePowerProduction
                  ? this.parameterColor('power')
                  : '#7cb5ec',
            },
          },
          labels: {
            format: isMetocean
              ? '{value} m'
              : this.havePowerProduction
                ? '{value} kWh'
                : this.showImperialUnits
                  ? '{value:%0.2f}in'
                  : '{value:%0.1f}mm',
            style: {
              color: isMetocean
                ? this.parameterColor('total_wave_hs')
                : this.havePowerProduction
                  ? this.parameterColor('power')
                  : '#7cb5ec',
            },
          },
          startOnTick: false,
          opposite: true,
          softMax: isMetocean ? 2 : this.showImperialUnits ? 0.1 : 4,
          min: 0,
        },
      ],
      credits: {
        enabled: !dashboardView,
        text: 'Wettermanufaktur GmbH',
        href: 'https://www.wettermanufaktur.de',
        style: { color: '#666' },
      },
      tooltip: {
        shared: true,
        useHTML: true,
        headerFormat:
          '<small style="color: #666">{point.x:%a, %d.%m. %H:%M}</small><br>',
        backgroundColor: isDark
          ? 'rgba(42, 42, 44, 0.85)'
          : 'rgba(255, 255, 255, 0.85)',
        style: { color: textColor },
      },

      series: this.highChartsData(),
      exporting: {
        enabled: !dashboardView,
        buttons: {
          contextButton: {
            menuItems: [
              'viewFullscreen',
              // 'separator',
              // 'downloadPNG',
              // 'downloadJPEG',
              // 'downloadPDF',
            ],
            enabled: true,
          },
        },
      },
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 500,
            },
            chartOptions: {
              chart: {
                marginLeft: 40,
                marginRight: 55,
              },
              // Dashboard tile on a phone: axis labels at the normal size
              // crowd a chart this narrow, so shrink them here too - same
              // dashboardView scoping as the legend above.
              xAxis: dashboardView
                ? [
                    {
                      labels: { style: { fontSize: '9px' } },
                    },
                  ]
                : undefined,
              legend: {
                // Dashboard tile: still roomy enough for a compact legend
                // once it's the only thing below the chart. Keep it hidden
                // on the full (non-dashboard) page at this width though -
                // that view already has more going on below the chart.
                enabled: dashboardView,
                floating: false,
                layout: 'horizontal',
                align: 'center',
                verticalAlign: 'bottom',
                itemStyle: { fontSize: '10px' },
                itemMarginTop: 0,
                itemMarginBottom: 2,
                x: 0,
                y: 0,
              },
              yAxis: [
                {
                  // Primary yAxis
                  labels: {
                    format: isMetocean
                      ? '{value} kn'
                      : '{value}°' + (this.showImperialUnits ? 'F' : 'C'),
                    style: dashboardView ? { fontSize: '9px' } : undefined,
                  },
                  title: {
                    enabled: false,
                    text: isMetocean
                      ? this.translate.instant('METPARA.ChartFF', {
                          unit: 'kn',
                        })
                      : this.translate.instant('METPARA.ChartTT', {
                          unit: this.showImperialUnits ? '°F' : '°C',
                        }),
                  },
                  softMin: 0,
                  plotLines: isMetocean
                    ? []
                    : [
                        {
                          value: 0,
                          color: '#9e9e9e',
                          width: 1,
                          zIndex: 2,
                        },
                      ],
                  startOnTick: false,
                  // maxPadding: 0.3,
                  // minRange: 8,
                },
                {
                  // Secondary yAxis
                  gridLineWidth: 0,
                  title: {
                    enabled: false,
                    text: isMetocean
                      ? this.translate.instant('METPARA.ChartWaveHeight', {
                          unit: 'm',
                        })
                      : this.translate.instant('METPARA.ChartRR', {
                          unit: this.showImperialUnits ? 'in' : 'mm',
                        }),
                    style: {
                      color: Highcharts.getOptions().colors[0],
                    },
                  },
                  labels: {
                    format: isMetocean
                      ? '{value} m'
                      : this.showImperialUnits
                        ? '{value:%0.2f}in'
                        : '{value:%0.1f}mm',
                    style: {
                      color: Highcharts.getOptions().colors[0],
                      fontSize: dashboardView ? '9px' : undefined,
                    },
                  },
                  startOnTick: false,
                  opposite: true,
                  softMax: isMetocean ? 2 : this.showImperialUnits ? 0.1 : 4,
                  min: 0,
                },
              ],
            },
          },
        ],
      },
    };
  }

  isHiddenParameter(parameter: string): boolean {
    if (parameter === 'dtg' || parameter === 'dayNight') {
      return false;
    }
    if (!this.isParameterAvailableForResolution(parameter)) {
      return true;
    }
    for (const userParameter of this.userParameters()) {
      if (userParameter.parameter === parameter) {
        return !userParameter.selected;
      }
    }
    return true;
  }

  isParameterAvailableForResolution(parameter: string): boolean {
    const hourly = this.resolution().hourly;
    return this.parameterDefinitions().some(
      p => parameter === p.fe_parameter && hourly === p.hourly
    );
  }

  updateChart(): void {
    this.updateFlag = true;
  }

  private setChartData() {
    this.highChartsData.set([]);

    const lineData = this.getHighChartsLineData();
    const barData = this.getHighChartsBarData();
    this.highChartsData.set([...lineData.concat(barData)]);
  }

  private listenToThemeChanges(): void {
    effect(
      () => {
        this.themeService.theme();

        untracked(() => {
          if (!this.eppData()) {
            return;
          }

          this.setChartData();
          this.setHighChartOptions();
          this.updateChart();
        });
      },
      { injector: this.injector }
    );
  }

  private listenToRebuildChanges() {
    effect(
      () => {
        this.rebuild();

        untracked(() => {
          if (this.eppData()) {
            this.setChartData();
            this.setHighChartOptions();
            this.updateChart();
          }
        });
      },
      { injector: this.injector }
    );
  }
}
